import { useEffect, useRef, useState } from "react"
import { Beauty, GlassesEffect } from "services/player"
import { type RuntimeGlassesControls } from "services/player/glasses-effect"
import { type ProductV2 } from "types"
import { createPlayer, isMountedContainer, mount, unmount } from "../../services/player/player"
import { fractionalRgbToHex, hexOrRgbToRgbString } from "../ColorItem/helpers"
import { ProductCategories } from "components/Navbar/types"

interface TintViewerProps {
  productItem?: ProductV2
  image: string
  size?: number
  activeColor?: string
  effectType?: ProductCategories
  glassesTransformMode?: "raw" | "controlled"
  onGlassesTransformsResolved?: (value: RuntimeGlassesControls | null) => void
}

export const TintViewer = ({
  productItem,
  image,
  size,
  activeColor,
  effectType,
  glassesTransformMode = "raw",
  onGlassesTransformsResolved,
}: TintViewerProps) => {
  const hideComparison = effectType === ProductCategories.GLASSES
  const [player] = useState(() => createPlayer())
  const [imageReady, setImageReady] = useState(false)
  const [startEffects, setStartEffects] = useState(false)
  const [sliderValue, setSliderValue] = useState(50)
  const [transitionStyle, setTransitionStyle] = useState<"none" | string>("none")
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const effectRequestRef = useRef(0)
  const imageRequestRef = useRef(0)

  useEffect(() => {
    let cancelled = false

    player.then(() => {
      const container = containerRef.current
      if (cancelled || !container) return

      void GlassesEffect.resetGlasses()
      mount(container)
      setStartEffects(true)
    })

    return () => {
      cancelled = true
      effectRequestRef.current += 1
      void GlassesEffect.resetGlasses()

      const container = containerRef.current
      if (!container || !isMountedContainer(container)) return

      unmount(container)
    }
  }, [player])

  useEffect(() => {
    let cancelled = false
    const requestId = imageRequestRef.current + 1
    imageRequestRef.current = requestId
    effectRequestRef.current += 1
    setImageReady(false)

    player
      .then(async () => {
        if (cancelled || !containerRef.current || !isMountedContainer(containerRef.current)) return
        await Beauty.applyImage(image)

        if (
          cancelled ||
          imageRequestRef.current !== requestId ||
          !containerRef.current ||
          !isMountedContainer(containerRef.current)
        )
          return

        setImageReady(true)
      })
      .catch((error) => {
        console.error(error)
        if (!cancelled && imageRequestRef.current === requestId) setImageReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [image, player])

  useEffect(() => {
    setTransitionStyle("none")
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTransitionStyle("opacity 0.5s ease-in-out")
      })
    })
    return () => cancelAnimationFrame(raf)
  }, [image])

  const applyMakeupEffect = () => {
    if (!productItem) return
    const { region, finish, coverage } = productItem
    const makeupRegion = `makeup_${region}`
    const color = activeColor ? fractionalRgbToHex(hexOrRgbToRgbString(activeColor)) : undefined
    Beauty.setMakeupState(makeupRegion, finish, coverage, color ?? "")
  }

  const applyCustomGlasses = async () => {
    if (!productItem) return
    const requestId = effectRequestRef.current + 1
    effectRequestRef.current = requestId
    const isCurrent = () =>
      effectRequestRef.current === requestId &&
      Boolean(containerRef.current) &&
      isMountedContainer(containerRef.current)

    try {
      if (productItem.effectUrl) {
        await GlassesEffect.applyDigitizedGlassesEffectFromUrl(productItem.effectUrl, {
          isCurrent,
        })

        if (isCurrent() && onGlassesTransformsResolved) {
          const runtimeControls = await GlassesEffect.readRuntimeGlassesControls({ isCurrent })
          if (isCurrent()) onGlassesTransformsResolved(runtimeControls)
        }
      } else if (isCurrent()) {
        await Beauty.reset()
        onGlassesTransformsResolved?.(null)
      }
    } catch (error) {
      console.error(error)
      if (isCurrent()) onGlassesTransformsResolved?.(null)
    }
  }

  useEffect(() => {
    if (!startEffects || !imageReady || !player || !productItem) return
    if (effectType === ProductCategories.MAKEUP) applyMakeupEffect()
  }, [
    effectType,
    productItem?.region,
    productItem?.finish,
    productItem?.coverage,
    activeColor,
    imageReady,
    player,
    startEffects,
  ])

  useEffect(() => {
    if (
      !startEffects ||
      !imageReady ||
      !player ||
      !productItem ||
      effectType !== ProductCategories.GLASSES
    )
      return
    void applyCustomGlasses()
  }, [effectType, imageReady, productItem?.effectUrl, player, startEffects])

  useEffect(() => {
    if (
      !startEffects ||
      !player ||
      !productItem ||
      effectType !== ProductCategories.GLASSES ||
      glassesTransformMode !== "controlled" ||
      !productItem.renderingOptions
    )
      return

    const requestId = effectRequestRef.current
    const isCurrent = () =>
      effectRequestRef.current === requestId &&
      Boolean(containerRef.current) &&
      isMountedContainer(containerRef.current)

    void GlassesEffect.setGlassesRenderingOptions(productItem.renderingOptions, { isCurrent })
  }, [effectType, glassesTransformMode, player, productItem?.renderingOptions, startEffects])

  const handleMouseDown = () => {
    setIsDragging(true)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      const rect = e.currentTarget.getBoundingClientRect()
      const offsetX = e.clientX - rect.left
      const newValue = Math.max(0, Math.min(100, (offsetX / rect.width) * 100))
      setSliderValue(newValue)
    }
  }

  return (
    <div
      className="relative aspect-square w-full"
      style={size ? { maxWidth: size } : undefined}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div ref={containerRef} className="h-full w-full" />
      </div>
      {!hideComparison && (
        <img
          src={image}
          alt="Old"
          className="absolute inset-0 h-full w-full object-cover"
          style={{
            clipPath: hideComparison ? "none" : `inset(0 ${100 - sliderValue}% 0 0)`,
            opacity: imageReady ? 1 : 0,
            transition: transitionStyle,
          }}
        />
      )}
      {!hideComparison && (
        <div
          className="absolute top-0 h-full"
          style={{
            left: `${sliderValue}%`,
            width: "2px",
            background: "#aaaac0",
            opacity: imageReady ? 1 : 0,
            transition: transitionStyle,
          }}
        />
      )}
      {!hideComparison && (
        <div
          className="absolute bottom-1 transform -translate-x-1/2 cursor-grab"
          style={{
            left: `${sliderValue}%`,
            opacity: imageReady ? 0.7 : 0,
            transition: transitionStyle,
          }}
          onMouseDown={handleMouseDown}
        >
          <button
            className="w-7 h-10 rotate-90 rounded-full !bg-white border border-gray-300 shadow-md flex items-center justify-center"
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="black"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 9l4-4 4 4m0 6l-4 4-4-4"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  )
}
